import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { Search, ChevronRight } from "lucide-react";
import { formatDateWithTimezone } from "../../../../../agent/src/common/time";
import { type RouterOutputs } from "../../../trpc";

type ListingRequest = RouterOutputs["listingRequest"]["list"]["data"][number];

type ListingRequestListPanelProps = {
  requests: ListingRequest[];
  selectedRequestId?: string;
  onSelect: (requestId: string) => void;
  searchPhone: string;
  onSearchChange: (searchPhone: string) => void;
  hasMore?: boolean;
  isLoading?: boolean;
  onLoadMore?: () => void;
  country?: string;
};

const ListingRequestListPanel = ({
  requests,
  selectedRequestId,
  onSelect,
  searchPhone,
  onSearchChange,
  hasMore = false,
  isLoading = false,
  onLoadMore,
  country = "CO",
}: ListingRequestListPanelProps) => {
  const isMobile = useIsMobile();

  const getListingTypeBadge = (type: string) => {
    switch (type) {
      case "rent":
        return (
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
            Arriendo
          </span>
        );
      case "sale":
        return (
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">
            Venta
          </span>
        );
      case "rent_and_sale":
        return (
          <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-800">
            Arriendo y Venta
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800">
            {type}
          </span>
        );
    }
  };

  const getStatusBadge = (status: "ocupado" | "desocupado") => {
    return status === "ocupado" ? (
      <span className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-semibold text-orange-800">
        Ocupado
      </span>
    ) : (
      <span className="inline-flex items-center rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-semibold text-teal-800">
        Desocupado
      </span>
    );
  };

  const getPropertyTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      studio: "Estudio",
      apartment: "Apartamento",
      warehouse: "Bodega",
      house: "Casa",
      medical_office: "Consultorio",
      building: "Edificio",
      farm: "Finca",
      room: "Habitación",
      hotel: "Hotel",
      commercial_space: "Local Comercial",
      lot: "Lote",
      office: "Oficina",
      parking: "Parqueadero",
    };
    return labels[type] || type;
  };

  // Note: filtering is now done at the backend level via the API
  const filteredRequests = requests;

  return (
    <div className="flex h-full w-full flex-col bg-white">
      {/* Header with search */}
      <div className="border-b border-gray-200 p-4">
        <p className="mb-3 text-lg font-bold">
          Captación de Inmuebles
        </p>
        <div className="relative">
          <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
            <Search className="size-4 text-gray-400" />
          </div>
          <Input
            placeholder="Buscar por teléfono..."
            value={searchPhone}
            onChange={(e) => {
              const numericValue = e.target.value.replace(/\D/g, "");
              onSearchChange(numericValue);
            }}
            className="pl-10"
          />
        </div>
      </div>

      {/* Column-based List */}
      <div className="flex-1 overflow-x-auto overflow-y-auto">
        {isLoading && filteredRequests.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <Spinner />
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <p className="text-sm text-gray-500">
              {searchPhone
                ? "No se encontraron solicitudes con ese teléfono"
                : "No hay solicitudes de captación"}
            </p>
          </div>
        ) : (
          <div className="flex flex-col p-0 md:p-4">
            {/* Header Row — hidden on mobile */}
            {!isMobile && (
              <div className="sticky top-0 z-[1] flex items-center gap-3 border-b border-gray-200 bg-gray-50 p-3 text-xs font-semibold uppercase text-gray-600">
                <div className="shrink-0 basis-[100px]">Fecha</div>
                <div className="min-w-[150px] flex-[2]">Cliente</div>
                <div className="min-w-[120px] flex-1">Teléfono</div>
                <div className="min-w-[100px] flex-1">Tipo</div>
                <div className="min-w-[120px] flex-1">Propiedad</div>
                <div className="min-w-[150px] flex-[1.5]">Ciudad</div>
                <div className="min-w-[100px] flex-1">Estado</div>
                <div className="shrink-0 basis-[80px]">Acción</div>
              </div>
            )}

            {/* Data Rows */}
            {filteredRequests.map((request) =>
              isMobile ? (
                <div
                  key={request.id}
                  className={cn(
                    "min-h-[44px] cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50",
                    selectedRequestId === request.id && "bg-blue-50"
                  )}
                  onClick={() => onSelect(request.id)}
                >
                  <div className="mb-1 flex items-center justify-between">
                    <p className="truncate text-sm font-medium">
                      {request.leadName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatDateWithTimezone(request.createdAt, country).split(" ")[0]}
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-600">{request.leadPhone || "-"}</p>
                    {getListingTypeBadge(request.listingType)}
                  </div>
                </div>
              ) : (
                <div
                  key={request.id}
                  className={cn(
                    "flex min-h-[60px] cursor-pointer items-center gap-3 border-b border-gray-100 p-3 transition-colors duration-200",
                    selectedRequestId === request.id ? "bg-blue-50" : "hover:bg-gray-50"
                  )}
                  onClick={() => onSelect(request.id)}
                >
                  {/* Date Column */}
                  <div className="shrink-0 basis-[100px] whitespace-nowrap text-xs">
                    {formatDateWithTimezone(request.createdAt, country).split(" ")[0]}
                  </div>

                  {/* Client Name Column */}
                  <div className="min-w-[150px] flex-[2] text-sm font-medium">
                    <p className="break-words">{request.leadName}</p>
                  </div>

                  {/* Phone Column */}
                  <div className="min-w-[120px] flex-1 whitespace-nowrap text-xs text-gray-600">
                    {request.leadPhone || "-"}
                  </div>

                  {/* Listing Type Column */}
                  <div className="min-w-[100px] flex-1">
                    {getListingTypeBadge(request.listingType)}
                  </div>

                  {/* Property Type Column */}
                  <div className="min-w-[120px] flex-1">
                    <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-semibold text-purple-800 [white-space:normal] [word-break:break-word]">
                      {getPropertyTypeLabel(request.propertyType)}
                    </span>
                  </div>

                  {/* City Column */}
                  <div className="min-w-[150px] flex-[1.5] text-xs text-gray-600">
                    <p className="break-words">{request.city || "-"}</p>
                  </div>

                  {/* Status Column */}
                  <div className="min-w-[100px] flex-1">
                    {getStatusBadge(request.status)}
                  </div>

                  {/* Action Column */}
                  <div className="shrink-0 basis-[80px]">
                    <button
                      className="inline-flex size-9 items-center justify-center rounded-md text-blue-600 hover:bg-blue-50"
                      aria-label="Ver detalles"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(request.id);
                      }}
                    >
                      <ChevronRight className="size-4" />
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>

      {/* Load More Button */}
      {hasMore && !isLoading && (
        <div className="border-t border-gray-200 p-4">
          <Button
            size="sm"
            variant="outline"
            className="w-full"
            onClick={onLoadMore}
          >
            Cargar más
          </Button>
        </div>
      )}

      {isLoading && filteredRequests.length > 0 && (
        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center justify-center">
            <Spinner size="sm" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ListingRequestListPanel;
